// Single business-valuation model — powers the Valuation Calculator page AND the
// listing page's Valuation tab. Never re-derive this math elsewhere (same rule
// as lib/sbaMath.ts): a second copy is how two screens start disagreeing.
//
// Kept free of imports so `node --test` can load it directly (tests/).

// ── Guardrails ───────────────────────────────────────────────────────────────
/** Sanity ceiling for dollar inputs — blocks extra-zero typos without constraining real deals. */
export const MAX_DOLLARS = 1_000_000_000;
/** A multiple of 0 isn't a valuation, it's a typo. */
export const MIN_MULTIPLE = 0.5;
/** Small businesses trade in a narrow band; past 20x is a typo, not a valuation. */
export const MAX_MULTIPLE = 20;
/** Above this much SDE, buyers price on EBITDA (a hired manager runs it), not SDE. */
export const EBITDA_BASIS_THRESHOLD = 1_000_000;
/** SDE adds back ONE working owner's pay. Above this it's usually two partners' salaries. */
export const OWNER_COMP_WARN = 250_000;
/** One-time add-backs above this share of SDE are the classic way SDE gets padded. */
export const ONE_TIME_ADDBACK_WARN_SHARE = 0.2;

export type ValuationBasis = "sde" | "ebitda";
export type BasisChoice = ValuationBasis | "auto";

export interface ValuationInputs {
  revenue: number;
  /** Pre-tax, as reported. Can be negative. */
  netProfit: number;
  /** ONE working owner's salary + benefits. A second partner's pay stays an expense. */
  ownerComp: number;
  depreciation: number;
  interest: number;
  oneTime: number;

  basis: BasisChoice;
  sdeMultipleLow: number;
  sdeMultipleHigh: number;
  ebitdaMultipleLow: number;
  ebitdaMultipleHigh: number;

  /** Saleable stock — added on top of the earnings value. */
  inventory: number;
  /** FF&E — already inside a small-business multiple, so it only sets the asset floor. */
  equipment: number;
  /** Working capital (cash / receivables) the seller leaves in the business — added on top. */
  workingCapital: number;

  /** Seller debt repaid from proceeds at close. */
  debt: number;
  /** Optional: what the seller is asking. 0 = not entered. */
  askingPrice: number;
  /** Part of the price the seller carries as a note. */
  sellerNote: number;
  /** Part of the price paid later, contingent on performance. */
  earnout: number;
}

export const VALUATION_DEFAULTS: ValuationInputs = {
  revenue: 1_200_000,
  netProfit: 140_000,
  ownerComp: 95_000,
  depreciation: 25_000,
  interest: 12_000,
  oneTime: 8_000,
  basis: "auto",
  sdeMultipleLow: 2.5,
  sdeMultipleHigh: 3.5,
  ebitdaMultipleLow: 3.5,
  ebitdaMultipleHigh: 5,
  inventory: 40_000,
  equipment: 60_000,
  workingCapital: 0,
  debt: 0,
  askingPrice: 0,
  sellerNote: 0,
  earnout: 0,
};

export type EarningsRating = { label: string; tone: "excellent" | "good" | "ok" | "risk" };

export interface ValuationResult {
  sde: number;
  /** Adjusted EBITDA: same non-owner add-backs as SDE (incl. one-time), owner pay stays an expense. */
  adjustedEbitda: number;
  basis: ValuationBasis;
  basisWasAuto: boolean;
  /** The earnings figure the multiple is applied to. */
  earnings: number;
  multipleLow: number;
  multipleHigh: number;
  /** earnings × multiple — the cash-flow price, before inventory / working capital. */
  enterpriseLow: number;
  enterpriseMid: number;
  enterpriseHigh: number;
  /** Inventory + equipment: what the business is worth as a pile of assets. */
  assetFloor: number;
  /** True when earnings can't support a multiple and the range is the asset floor. */
  assetsOnly: boolean;
  low: number;
  mid: number;
  high: number;
  equityLow: number;
  equityMid: number;
  equityHigh: number;
  sdeMargin: number;
  /** Margin with one-time add-backs stripped out — what the rating is based on. */
  recurringMargin: number;
  /** enterpriseMid ÷ revenue: excludes inventory so it's comparable to published revenue multiples. */
  impliedRevenueMultiple: number;
  rating: EarningsRating;
  ask: AskAnalysis | null;
  structure: DealStructure;
  warnings: string[];
}

export interface AskAnalysis {
  askingPrice: number;
  position: "below" | "within" | "above";
  /** % above (+) or below (−) the midpoint. */
  vsMidPct: number;
  /** (ask − inventory − working capital) ÷ earnings — the multiple the seller is really asking. null if earnings ≤ 0. */
  impliedMultiple: number | null;
}

export interface DealStructure {
  /** The price the structure is built on: the ask if entered, else the midpoint. */
  price: number;
  priceIsAsk: boolean;
  sellerNote: number;
  earnout: number;
  /** price − seller note − earnout: what has to be funded on closing day. */
  cashAtClose: number;
  /** cashAtClose − debt: what the seller actually pockets at closing. */
  sellerNetAtClose: number;
}

export function resolveBasis(choice: BasisChoice, sde: number): ValuationBasis {
  if (choice !== "auto") return choice;
  return sde >= EBITDA_BASIS_THRESHOLD ? "ebitda" : "sde";
}

/** Rated on RECURRING margin, so padded one-time add-backs can't buy an "Excellent". */
export function marginRating(marginPct: number): EarningsRating {
  if (marginPct >= 25) return { label: "Excellent", tone: "excellent" };
  if (marginPct >= 15) return { label: "Healthy", tone: "good" };
  if (marginPct >= 8) return { label: "Acceptable", tone: "ok" };
  return { label: "Thin", tone: "risk" };
}

export function computeValuation(i: ValuationInputs): ValuationResult {
  // SDE — what a single owner-operator takes out of the business.
  const sde = i.netProfit + i.ownerComp + i.depreciation + i.interest + i.oneTime;
  // Adjusted EBITDA — the manager-run view: owner pay stays an expense (it
  // becomes a manager's salary), every other add-back matches SDE.
  const adjustedEbitda = i.netProfit + i.depreciation + i.interest + i.oneTime;

  const basis = resolveBasis(i.basis, sde);
  const earnings = basis === "sde" ? sde : adjustedEbitda;
  const multipleLow = basis === "sde" ? i.sdeMultipleLow : i.ebitdaMultipleLow;
  const multipleHigh = basis === "sde" ? i.sdeMultipleHigh : i.ebitdaMultipleHigh;

  const assetFloor = i.inventory + i.equipment;
  const assetsOnly = earnings <= 0;

  const enterpriseLow = assetsOnly ? 0 : earnings * multipleLow;
  const enterpriseHigh = assetsOnly ? 0 : earnings * multipleHigh;
  const enterpriseMid = (enterpriseLow + enterpriseHigh) / 2;

  // Equipment is already priced into the multiple, so only inventory and working
  // capital go on top. A business is never worth less than its saleable assets.
  const onTop = i.inventory + i.workingCapital;
  const low = Math.max(enterpriseLow + onTop, assetFloor);
  const high = Math.max(enterpriseHigh + onTop, assetFloor);
  const mid = (low + high) / 2;

  const sdeMargin = i.revenue > 0 ? (sde / i.revenue) * 100 : 0;
  const recurringMargin = i.revenue > 0 ? ((sde - i.oneTime) / i.revenue) * 100 : 0;
  const impliedRevenueMultiple = i.revenue > 0 ? enterpriseMid / i.revenue : 0;

  let ask: AskAnalysis | null = null;
  if (i.askingPrice > 0) {
    ask = {
      askingPrice: i.askingPrice,
      position: i.askingPrice < low ? "below" : i.askingPrice > high ? "above" : "within",
      vsMidPct: mid > 0 ? ((i.askingPrice - mid) / mid) * 100 : 0,
      impliedMultiple: earnings > 0 ? (i.askingPrice - onTop) / earnings : null,
    };
  }

  const price = i.askingPrice > 0 ? i.askingPrice : mid;
  const cashAtClose = price - i.sellerNote - i.earnout;
  const structure: DealStructure = {
    price,
    priceIsAsk: i.askingPrice > 0,
    sellerNote: i.sellerNote,
    earnout: i.earnout,
    cashAtClose,
    sellerNetAtClose: cashAtClose - i.debt,
  };

  // Things that make the number untrustworthy rather than merely low.
  const warnings: string[] = [];
  if (assetsOnly) {
    warnings.push(
      `${basis === "sde" ? "SDE" : "Adjusted EBITDA"} is zero or negative, so no earnings multiple applies — the range is the inventory + equipment value only`,
    );
  }
  if (sde > i.revenue) warnings.push("SDE exceeds revenue, which isn't possible — check the add-backs");
  if (i.netProfit < 0) warnings.push("The business is loss-making before add-backs");
  if (i.ownerComp > OWNER_COMP_WARN) {
    warnings.push(
      "Owner pay over $250k usually means more than one owner — SDE adds back one working owner's pay; a partner's salary must stay as an expense",
    );
  }
  const otherAddBacks = i.depreciation + i.interest + i.oneTime;
  if (i.netProfit > 0 && otherAddBacks > i.netProfit) {
    warnings.push("Add-backs (besides owner pay) are larger than the reported profit — ask for the general ledger behind them");
  }
  if (sde > 0 && i.oneTime > sde * ONE_TIME_ADDBACK_WARN_SHARE) {
    warnings.push(
      `One-time add-backs are ${Math.round((i.oneTime / sde) * 100)}% of SDE — get receipts; "one-time" costs that recur every year aren't add-backs`,
    );
  }
  const equityLow = low - i.debt;
  if (i.debt > 0 && equityLow < 0) {
    warnings.push(
      i.debt > high
        ? "Debt exceeds even the high valuation — the seller can't clear it from the sale"
        : "Debt exceeds the low valuation — at the bottom of the range the seller is underwater",
    );
  }
  if (i.sellerNote + i.earnout > price) {
    warnings.push("Seller note + earnout are larger than the price — nothing is left to pay at close");
  }

  return {
    sde,
    adjustedEbitda,
    basis,
    basisWasAuto: i.basis === "auto",
    earnings,
    multipleLow,
    multipleHigh,
    enterpriseLow,
    enterpriseMid,
    enterpriseHigh,
    assetFloor,
    assetsOnly,
    low,
    mid,
    high,
    equityLow,
    equityMid: mid - i.debt,
    equityHigh: high - i.debt,
    sdeMargin,
    recurringMargin,
    impliedRevenueMultiple,
    rating: marginRating(recurringMargin),
    ask,
    structure,
    warnings,
  };
}

// ── Industry presets ─────────────────────────────────────────────────────────
export interface IndustryMultiples {
  id: number;
  name: string;
  sde_multiple_low: number | null;
  sde_multiple_high: number | null;
  ebitda_multiple_low: number | null;
  ebitda_multiple_high: number | null;
}

export function hasPreset(c: IndustryMultiples): boolean {
  return (c.sde_multiple_low != null && c.sde_multiple_high != null) ||
    (c.ebitda_multiple_low != null && c.ebitda_multiple_high != null);
}

/** True when the buyer's multiples sit outside the industry's typical range. */
export function outsidePreset(c: IndustryMultiples, basis: ValuationBasis, low: number, high: number): boolean {
  const pLow = basis === "sde" ? c.sde_multiple_low : c.ebitda_multiple_low;
  const pHigh = basis === "sde" ? c.sde_multiple_high : c.ebitda_multiple_high;
  if (pLow == null || pHigh == null) return false;
  return low < pLow || high > pHigh;
}

// ── Share links ──────────────────────────────────────────────────────────────
export function toValuationQuery(i: ValuationInputs, industryId?: number | null): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(i)) q.set(k, String(v));
  if (industryId) q.set("industry", String(industryId));
  return q.toString();
}

/** Lenient: unknown or malformed params are ignored, so a stale link still loads. */
export function parseValuationQuery(search: string): { values: Partial<ValuationInputs>; industryId: number | null } {
  const q = new URLSearchParams(search);
  const out: Record<string, unknown> = {};
  for (const [k, def] of Object.entries(VALUATION_DEFAULTS)) {
    const v = q.get(k)?.trim();
    if (!v || typeof def !== "number") continue;
    if (Number.isFinite(Number(v))) out[k] = Number(v);
  }
  const basis = q.get("basis");
  if (basis === "auto" || basis === "sde" || basis === "ebitda") out.basis = basis;
  const industry = Number(q.get("industry"));
  return { values: out as Partial<ValuationInputs>, industryId: Number.isInteger(industry) && industry > 0 ? industry : null };
}

/** Query string that opens the SBA calculator on this deal (see parseSbaQuery). */
export function toSbaHandoffQuery(i: ValuationInputs, r: ValuationResult): string {
  const q = new URLSearchParams();
  const price = Math.round(r.structure.price);
  q.set("askingPrice", String(price));
  if (r.basis === "sde") {
    q.set("cashFlowBasis", "sde");
    q.set("cashFlow", String(Math.round(r.sde)));
    // The SBA model subtracts a replacement salary from SDE — the owner's own pay is the best proxy.
    q.set("ownerSalary", String(Math.round(i.ownerComp)));
  } else {
    q.set("cashFlowBasis", "ebitda");
    q.set("cashFlow", String(Math.round(r.adjustedEbitda)));
  }
  if (i.workingCapital > 0) q.set("workingCapital", String(Math.round(i.workingCapital)));
  if (i.sellerNote > 0 && price > 0) q.set("sellerNotePct", (Math.round((i.sellerNote / price) * 1000) / 10).toString());
  return q.toString();
}

// ── Export ───────────────────────────────────────────────────────────────────
export function valuationCsv(i: ValuationInputs, r: ValuationResult, industryName?: string | null): string {
  const rows: [string, string | number][] = [
    ["Industry", industryName ?? "Custom"],
    ["Annual revenue", i.revenue],
    ["Net profit (pre-tax)", i.netProfit],
    ["Owner salary & benefits (one owner)", i.ownerComp],
    ["Depreciation & amortization", i.depreciation],
    ["Interest expense", i.interest],
    ["One-time expenses", i.oneTime],
    ["SDE", Math.round(r.sde)],
    ["Adjusted EBITDA", Math.round(r.adjustedEbitda)],
    ["Valuation basis", r.basis.toUpperCase()],
    ["Multiple low", r.multipleLow],
    ["Multiple high", r.multipleHigh],
    ["Inventory", i.inventory],
    ["Equipment (FF&E, inside multiple)", i.equipment],
    ["Working capital included", i.workingCapital],
    ["Valuation low", Math.round(r.low)],
    ["Valuation mid", Math.round(r.mid)],
    ["Valuation high", Math.round(r.high)],
    ["Debt settled at close", i.debt],
    ["Seller equity low", Math.round(r.equityLow)],
    ["Seller equity mid", Math.round(r.equityMid)],
    ["Seller equity high", Math.round(r.equityHigh)],
    ["Asking price", i.askingPrice || ""],
    ["Seller note", i.sellerNote],
    ["Earnout", i.earnout],
    ["Cash at close", Math.round(r.structure.cashAtClose)],
    ["Seller net at close", Math.round(r.structure.sellerNetAtClose)],
  ];
  const esc = (v: string | number) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return ["Item,Value", ...rows.map(([k, v]) => `${esc(k)},${esc(v)}`), "", ...r.warnings.map((w) => `Warning,${esc(w)}`)].join("\n");
}
